import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { AuthContext } from './AuthContext';
import { shareBacktestResult } from '../apis/junkyard';
import {
  CreateShareableBacktestLinkRequest,
  CreateShareableBacktestLinkResponse,
} from '../utils/typesTrading';

interface ShareableBacktestLinkProps {
  open: boolean;
  onClose: () => void;
  backtestUniqueId: string | null;
}

function formatExpiration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 'soon';
  }
  const days = Math.floor(seconds / 86400);
  if (days >= 1) {
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) {
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 1) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const s = Math.floor(seconds);
  return `${s} second${s === 1 ? '' : 's'}`;
}

function buildShareableUrl(uniqueId: string): string {
  return `${window.location.origin}/backtest/shared/${uniqueId}`;
}

export default function ShareableBacktestLink({
  open,
  onClose,
  backtestUniqueId,
}: ShareableBacktestLinkProps) {
  const authContext = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [shareableUrl, setShareableUrl] = useState<string | null>(null);
  const [expirationSeconds, setExpirationSeconds] = useState<number | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [shareCode, setShareCode] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    // reset state on each open / option change
    setError(null);
    setShareableUrl(null);
    setExpirationSeconds(null);
    setCopied(false);

    if (!backtestUniqueId) {
      setError('This backtest must be saved before it can be shared.');
      return;
    }

    let cancelled = false;
    const doShare = async () => {
      setIsLoading(true);
      try {
        const resp = (await shareBacktestResult({
          access_token: authContext.readAccessToken(),
          backtest_unique_id: backtestUniqueId,
          share_code: shareCode,
        } as CreateShareableBacktestLinkRequest)) as CreateShareableBacktestLinkResponse;

        if (cancelled) return;

        if (resp.success && resp.backtest_unique_id) {
          setShareableUrl(buildShareableUrl(resp.backtest_unique_id));
          const expSec = resp.expiration_seconds
            ? parseFloat(resp.expiration_seconds as unknown as string)
            : NaN;
          setExpirationSeconds(Number.isFinite(expSec) ? expSec : null);
        } else {
          console.error("Error generating shareable link", resp);
          setError('Failed to generate a shareable link. Please try again.');
        }
      } catch (e) {
        console.error('Failed to share backtest result', e);
        if (!cancelled) {
          setError('An error occurred while generating a shareable link.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    doShare();

    return () => {
      cancelled = true;
    };
  }, [open, backtestUniqueId, authContext, shareCode]);

  const handleCopy = async () => {
    if (!shareableUrl) return;
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy link', e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            backgroundImage: 'none',
          },
        },
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 2 }}>Share Backtest</DialogTitle>
      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : shareableUrl ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Anyone with this link can view the backtest results
              {shareCode ? ' and the strategy code' : ' (the strategy code will not be shared)'}.
              The link will expire in{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {expirationSeconds !== null
                  ? formatExpiration(expirationSeconds)
                  : 'some time'}
              </Box>
              .
            </Typography>
            <TextField
              fullWidth
              value={shareableUrl}
              size="small"
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                        <IconButton onClick={handleCopy} size="small" sx={{ mr: -2 }}>
                          {copied ? (
                            <CheckIcon fontSize="small" />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
              onFocus={(e) => e.target.select()}
            />
          </Stack>
        ) : null}
        {!isLoading && !error && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={shareCode}
                  onChange={(e) => setShareCode(e.target.checked)}
                  size="small"
                />
              }
              label="Include strategy code in shared link"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {shareableUrl && (
          <Button onClick={handleCopy} variant="outlined">
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
